import { useContext, useEffect, useState } from 'react'
import { AudioEngineContext } from './engine/audio-engine-context.js'

/** Default input hook, reactive to handle changes in the devices, including permissions.
 *
 * Tolerates a missing `AudioEngineContext` value: returns `undefined` when no engine is
 * mounted. Consumers (`useTunerMenuItem`, `useMic`) already null-guard the result.
 *
 * Why this matters (GH #1202): `useAudioEngine()` throws when the context value is null,
 * which used to nuke the entire `<AppSideBar>` through the React error boundary whenever
 * the provider/consumer context identities desynced (HMR, suspended provider re-renders).
 * The sidebar's tuner menu item legitimately renders without an engine — it's just an
 * icon and a dialog trigger — so reading the context directly here is safer than relying
 * on `<AudioContainer>` always being mounted ahead of consumers.
 */
export const useDefaultInput = () => {
  const engine = useContext(AudioEngineContext)
  const [input, setInput] = useState(engine?.defaultInput)

  useEffect(() => {
    if (!engine) {
      setInput(undefined)
      return undefined
    }
    const handleChange = () => {
      setInput(engine.defaultInput)
    }
    setInput(engine.defaultInput)
    engine.addEventListener('devicechange', handleChange)
    return () => {
      engine.removeEventListener('devicechange', handleChange)
    }
  }, [engine])

  return input
}
